import { useCallback, useEffect, useRef, useState, useMemo, useLayoutEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector, useIsElectron } from 'app/hooks'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import {
  useCloseThreadMutation,
  useDeleteMessageMutation,
  useLazyGetJumpMessagesQuery,
  useGetMessagesByPageMutation,
  useUpdateMessageReactionMutation,
  useForwardMessageMutation,
} from 'app/services/conversation.service'
import {
  useCreateMeetingConversationMutation,
  useJoinPublicChannelMutation,
  useSendPinnedConversationsMutation,
  useUpdateRoomChannelApparanceMutation,
} from 'app/services/room.service'
import {
  CONFERENCE_BLANK_DIMENSIONS,
  CONFERENCE_TYPE_VIDEO,
  MOBILE_WIDTH,
  // SCROLL_THRESHOLD,
  SIDE_PANEL_PIN,
  SIDE_PANEL_SEARCH,
  SIDE_PANEL_READERS,
  SIDE_PANEL_THREAD,
} from 'utils/constants'

import { FixMeLater } from 'types'
import { IRoom, RoomTypes } from 'types/rooms.types'

import { ConversationSidePanelState, IMessage } from 'types/conversation.types'
import useInfoModal from 'utils/hooks/useInfoModal'
import { groupsActions } from 'app/slices/groups.slice'
import useUploadFiles from 'utils/hooks/useUploadFiles'
import { scrollBottom } from 'utils/helpers/scrollHelper'
import { useOutletState } from 'utils/hooks/useOutletState'
import { addUnreadSeparator, conversationActions } from 'app/slices/conversations.slice'
import { BUTTONS, ERRORS, MODALS, PLACEHOLDERS, TABS, TITLES } from 'locales/locales'
import { useLazyCreateChannelConferenceQuery } from 'app/services/conference.service'

import Loading from 'components/spinner/Loading'
import Dropzone from 'components/dropzone/Dropzone'
import MessageInput from 'components/input/message-input/MessageInput'
import PinMessages from 'components/conversation-side-panel/PinMessages'
import ThreadMessages from 'components/conversation-side-panel/ThreadMessages'
import SearchMessages from 'components/conversation-side-panel/SearchMessages'
import ChannelInfoModal from 'features/channels/channel-info/ChannelInfoModal'
import ConversationHeader from 'components/conversation-header/ConversationHeader'
import CustomConversationScroll from 'components/infinite-scroll/CustomConversationScroll'
import ConversationSidePanel from 'components/conversation-side-panel/ConversationSidePanel'
import Jumper from 'components/new-message/jumper/Jumper'
import ScrollDownButton from '../../components/infinite-scroll/ScrollDownButton'
import usePasteFile from 'utils/hooks/usePasteFile'
import Spinner from 'components/spinner/Spinner'
import { concatDaySeparator } from 'utils/helpers/messageHelper'
import { IGroup } from 'types/group.types'
import Forward from 'components/new-message/forward/Forward'
import _ from 'lodash'
import DropdownAlert from 'components/dropdown-alert/DropdownAlert'
import SelectionBar from 'components/new-message/selection-bar/SelectionBar'
import ReadStatusPanel from 'components/conversation-side-panel/ReadStatusPanel'
import { useCreateConference } from 'utils/hooks/useCreateConference'

const Conversation = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { onToggleConversationPanel, channelId, channel } = useOutletState()

  const infiniteScrollContainerRef = useRef<FixMeLater>()
  const sidePanelStateRef = useRef<ConversationSidePanelState>()
  const channelIdRef = useRef<string>()

  const { openModal, closeModal, Modal } = useInfoModal()
  const {
    openModal: openConferenceModal,
    closeModal: closeConferenceModal,
    Modal: ConferenceModal,
  } = useInfoModal()
  const { onUpdatePastedFileTargetId } = usePasteFile({})

  const myId = useAppSelector((state) => state.auth.id)
  const [isShowSpinner, setIsShowSpinner] = useState(false)
  const users = useAppSelector((state) => state.users.data)
  const { draftData, renderedRooms } = useAppSelector((state) => state.message)
  const messagesData = useAppSelector((state) => state.message.data)[channelId]
  const groups: IGroup[] = useAppSelector((state) => state.groups.data)
  const initialInputData = useAppSelector((state) => state.message.draftData[channelId]) || null
  const channelWithUsers = useAppSelector((state) => state.rooms.data.find((room: IRoom) => room?._id === channelId))
  const [selectionMode, setSelectionMode] = useState<{
    isSelectionMode: boolean
    selectedMessages: string[]
  }>({ isSelectionMode: false, selectedMessages: [] })
  const [threadSelectionMode, setThreadSelectionMode] = useState<{
    isSelectionMode: boolean
    selectedMessages: string[]
  }>({ isSelectionMode: false, selectedMessages: [] })
  const [showForwardToRoomsModal, setShowForwardToRoomsModal] = useState<boolean>(false);

  const [counter, setCounter] = useState(0)
  const [searchText, setSearchText] = useState('')
  const [jumpToIndex, setJumpToIndex] = useState(-1)
  const [isSearchInput, setSearchInput] = useState(false)
  const [visibleChannelInfo, setVisibleChannelInfo] = useState(false)
  const [isScrollAboveThreshold, setScrollAboveThreshold] = useState(false)
  const [conversationSidePanel, setConversationSidePanel] = useState<ConversationSidePanelState>({
    visible: false,
    type: undefined,
    params: undefined,
  })
  const [windowApi, setWindowApi] = useState<any>(null)
  const [showScrollButton, setShowScrollButton] = useState(false)

  const [deleteMessage] = useDeleteMessageMutation()
  const [getJumpMessages] = useLazyGetJumpMessagesQuery()
  const [joinPublicChannel] = useJoinPublicChannelMutation()
  const [getMessagesByPage] = useGetMessagesByPageMutation()
  const [updateMessageReaction] = useUpdateMessageReactionMutation()
  const [sendPinnedConversations] = useSendPinnedConversationsMutation()
  const [updateRoomChannelApparance] = useUpdateRoomChannelApparanceMutation()
  const [createConference] = useLazyCreateChannelConferenceQuery()
  const [closeThread] = useCloseThreadMutation()
  const [forwardMessageToRooms, { isLoading: isForwarding }] = useForwardMessageMutation()

  const {
    isDragging,
    getInputProps,
    onDragEnter,
    onDragOver,
    onDragLeave,
    onDrop,
    getRootProps,
    onUploadAudio,
    onDragAndDropEnd,
  } = useUploadFiles({ id: channelId })

  const onScrollBottom = useCallback(() => {
    scrollBottom(infiniteScrollContainerRef)
    setShowScrollButton(false)
  }, [])

  const location = useLocation()
  const isElectron = useIsElectron()
  useEffect(() => {
    const w = window as any
    if (w.api) setWindowApi(w.api)
  }, [])

  const handleToggleMessageSelection = useCallback((messageId: string) => {
    setSelectionMode((prev) =>
      prev.selectedMessages.includes(messageId)
        ? {
            ...prev,
            selectedMessages: prev.selectedMessages.filter((id: string) => id !== messageId),
          }
        : { ...prev, selectedMessages: [...prev.selectedMessages, messageId] },
    )
  }, [])

  const onCloseDropzone = useCallback(() => {
    onDragAndDropEnd()
  }, [onDragAndDropEnd])


  const handleCompleteSelection = useCallback(() => {
    if (selectionMode.selectedMessages.length > 0) {
      setShowForwardToRoomsModal(true)
    }
  }, [selectionMode.selectedMessages])

  const handleCancelSelection = useCallback(() => {
    setShowForwardToRoomsModal(false)
    setSelectionMode({ isSelectionMode: false, selectedMessages: [] })
  }, [setShowForwardToRoomsModal, setSelectionMode])

  const handleCloseForwardToRoomsModal = useCallback(() => {
    setShowForwardToRoomsModal(false)
  }, [setShowForwardToRoomsModal])

  const handleForward = useCallback(async (roomIds: string[]) => {
    try {
      const { data }: FixMeLater = await forwardMessageToRooms({
        messageIds: selectionMode.isSelectionMode
          ? selectionMode.selectedMessages
          : threadSelectionMode.selectedMessages,
        roomIds,
      })
      if (data?.success) {
        roomIds.forEach((roomId) => {
          data.data[roomId].forEach((message: IMessage) => {
            dispatch(
              conversationActions.sendMessage({
                ...message,
                roomId: roomId,
                sender: myId,
              }),
            )
          })
        })
      } else {
        DropdownAlert.ref.show({
          isError: true,
          message: t(ERRORS.FAILED_TO_FORWARD_MESSAGE),
        })
      }
    } catch (error) {
      console.error(error)
    } finally {
      setSelectionMode({ isSelectionMode: false, selectedMessages: [] })
      setThreadSelectionMode({ isSelectionMode: false, selectedMessages: [] })
      setShowForwardToRoomsModal(false)
    }
  },[selectionMode.selectedMessages, myId, dispatch, forwardMessageToRooms, t]);


  const handleToggleThreadSelection = useCallback((messageId: string) => {
    setThreadSelectionMode(prev =>
      prev.selectedMessages.includes(messageId)
        ? { ...prev, selectedMessages: prev.selectedMessages.filter((id: string) => id !== messageId) }
        : { ...prev, selectedMessages: [...prev.selectedMessages, messageId] }
    );

  }, []);

  useEffect(() => {
    const w = window as FixMeLater
    if (w.api) setWindowApi(w.api)
  }, [])

  useEffect(() => {
    if (selectionMode?.isSelectionMode) {
      setThreadSelectionMode({ isSelectionMode: false, selectedMessages: [] })
    }
  }, [selectionMode?.isSelectionMode])

  useEffect(() => {
    if (threadSelectionMode?.isSelectionMode) {
      setSelectionMode({ isSelectionMode: false, selectedMessages: [] })
    }
  }, [threadSelectionMode?.isSelectionMode])

  useEffect(() => {
    sidePanelStateRef.current = conversationSidePanel
  }, [conversationSidePanel])

  useEffect(() => {
    const teamOfChannel = groups.find((group: IGroup) => group._id === channel.groupId)
    // if team of the channel is archived, navigate to another random team which is unarchived,
    // if there is not such unarchived existing team, navigate to '/channels' instead.
    if (teamOfChannel?.isArchived) {
      const unarchivedTeam = groups.find((group) => !group.isArchived)
      if (unarchivedTeam) {
        const generalChannelOfUnarchivedTeam = unarchivedTeam.channels.find(
          (c) => c.name.toLowerCase() === 'general',
        )
        navigate('/channels/' + generalChannelOfUnarchivedTeam?._id)
      } else {
        navigate('/channels')
      }
    }
  }, [channel])

  useEffect(() => {
    if (location.state?.action === 'JUMP_TO_THREAD') {
      const { threadId, messageId, roomId } = location.state

      setConversationSidePanel({
        visible: true,
        type: SIDE_PANEL_THREAD,
        params: { threadId, messageId, roomId, users, myId },
      })
    }
  }, [location.state, users, myId])

  useLayoutEffect(() => {
    const syncSteps = async () => {
      if (!messagesData?.messages?.length || !messagesData) {
        await getInitialData();
        dispatch(addUnreadSeparator({ roomId: channelId }));
      } else if (messagesData?.page === 0 && !renderedRooms.includes(channelId)) {
        dispatch(conversationActions.clearRoomMessages({ roomId: channelId }));
        await getInitialData();
        dispatch(addUnreadSeparator({ roomId: channelId }));
      }
      onScrollBottom();
      setSearchInput(false);
      channelIdRef.current = channelId;
      dispatch(conversationActions.updateRenderedRooms(channelId));
      setConversationSidePanel((prev) => {
        return { ...prev, visible: false };
      });
      setThreadSelectionMode({ isSelectionMode: false, selectedMessages: [] });
      setSelectionMode({ isSelectionMode: false, selectedMessages: [] });
    };
    syncSteps();
    return () => {
      dispatch(groupsActions.updateUnreadCount({ roomId: channelId, increase: false }));
      dispatch(conversationActions.removeUnreadSep({ roomId: channelId }));
    };
  }, [channelId]);

  //MESSAGE JUMPER
  useEffect(() => {
    if (messagesData?.newMessage && isScrollAboveThreshold && messagesData?.messages?.length) {
      onIncreaseMessageJumperCount()
    }
  }, [messagesData?.messages?.length, messagesData?.newMessage])

  const onChangeSearch = (text: string) => {
    setSearchText(text)
    if (text.trim().length > 0) {
      onToggleConversationSidePanel(true, SIDE_PANEL_SEARCH)
    }
  }

  const onToggleConversationSidePanel = useCallback(
    (visible: boolean, type?: string, params?: { messageId: string; threadId?: string }) => {
      setConversationSidePanel({ visible, type, params })

      const threadId = params?.messageId ?? conversationSidePanel.params?.messageId
      if (!visible && conversationSidePanel.type === SIDE_PANEL_THREAD) {
        closeThread({
          roomId: channelId,
          threadId,
        })
      } else if (visible && type === SIDE_PANEL_THREAD) {
        // if switched from one thread to another while panel is open, close previous one.
        if (conversationSidePanel.params?.messageId) {
          closeThread({
            roomId: channelId,
            threadId: conversationSidePanel.params?.messageId,
          })
        }
      }
    },
    [conversationSidePanel, channelId, closeThread],
  )



  const [createMeetingConversation] = useCreateMeetingConversationMutation()

  const { handleCreateConference } = useCreateConference({
    createConference,
    createMeetingConversation,
    isElectron,
    windowApi,
    closeConferenceModal,
  });

  const onVideoCall = async (members: string[]) => {
    openConferenceModal({
      title: t(MODALS.CREATE_VIDEO_CONFERENCE_TITLE),
      description: t(MODALS.CREATE_VIDEO_CONFERENCE_DESCRIPTION),
      cancelTitle: t(BUTTONS.CANCEL) as string,
      okTitle: t(BUTTONS.YES) as string,
      onCancel: closeConferenceModal,
      onOk: () => handleCreateConference(CONFERENCE_TYPE_VIDEO, members, channelId),
    })
  }



  const getInitialData = useCallback(async () => {
    try {
      setIsShowSpinner(true)
      await getMessagesByPage({ roomId: channelId, pageNumber: 0 })
    } finally {
      setIsShowSpinner(false)
    }
  }, [channelId, getMessagesByPage])

  const onPagination = useCallback(() => {
    getMessagesByPage({ roomId: channelId, pageNumber: messagesData?.page + 1 || 0 })
  }, [channelId, messagesData?.page, getMessagesByPage])

  const toggleChannelInfo = useCallback(() => {
    if (channel.hasSubscription !== false) setVisibleChannelInfo(!visibleChannelInfo)
  }, [channel.hasSubscription, visibleChannelInfo])

  const onUnarchiveChannel = useCallback(async () => {
    try {
      const result: FixMeLater = await updateRoomChannelApparance({
        roomId: channelId,
        isAppear: false,
      })
      dispatch(groupsActions.updateApperance(result.data))
      navigate(result.data.redirectUrl)
      dispatch(conversationActions.removeAllDraftAndFailedMessages(channelId))
    } catch (e) {
      console.log(e)
    }
  }, [channelId, updateRoomChannelApparance, dispatch, navigate])

  const onJumpMessage = useCallback(
    async (messageId: string, threadId?: string) => {
      if (threadId) {
        onToggleConversationSidePanel(true, SIDE_PANEL_THREAD, { messageId, threadId })
      } else {
        const { data }: FixMeLater = await getJumpMessages({ roomId: channelId, messageId })
        dispatch(conversationActions.getJumpMessages(data))
        const clonedData = _.cloneDeep([...data.data])
        const reStructeredMessages = concatDaySeparator(clonedData)
        const index: number = reStructeredMessages.findIndex(
          (message: IMessage) => message._id === messageId,
        )
        setJumpToIndex(index)
      }
      setSearchInput(false)
    },
    [channelId, getJumpMessages, dispatch, onToggleConversationSidePanel],
  )

  const onTogglePinMessage = useCallback(
    (messageId: string, pinned: boolean) => {
      sendPinnedConversations({ roomId: channelId, messageId, pinned })
    },
    [channelId, sendPinnedConversations],
  )

  const onApproveDeleteMessage = useCallback(
    async (messageId: string) => {
      deleteMessage({ messageId }).finally(closeModal)
    },
    [deleteMessage, closeModal],
  )

  const onDeleteMessage = useCallback(
    (messageId: string) => {
      openModal({
        title: t(MODALS.DELETE_MESSAGE_TITLE),
        description: t(MODALS.DELETE_MESSAGE_DESCRIPTION),
        cancelTitle: t(BUTTONS.CANCEL) as string,
        okTitle: t(BUTTONS.DELETE) as string,
        onCancel: closeModal,
        onOk: () => onApproveDeleteMessage(messageId),
      })
    },
    [openModal, closeModal, onApproveDeleteMessage],
  )

  const onReactionMessage = useCallback(
    async (messageId: string, emoji: string, isAdd: boolean) => {
      await updateMessageReaction({ messageId, emoji, add: isAdd })
    },
    [updateMessageReaction],
  )

  const onJoinChannel = useCallback(async () => {
    await joinPublicChannel({ channelId })
  }, [channelId, joinPublicChannel])

  const onAppearChannel = useCallback(async () => {
    try {
      const result: FixMeLater = await updateRoomChannelApparance({
        roomId: channelId,
        isAppear: true,
      })
      dispatch(groupsActions.updateApperance(result.data))
    } catch (e) {
      console.log(e)
    }
  }, [channelId, updateRoomChannelApparance, dispatch])

  //FOR MESSAGE JUMPER
  const onCloseMessageJumper = useCallback(() => {
    setCounter(0)
  }, [])

  //FOR MESSAGE JUMPER
  const onIncreaseMessageJumperCount = useCallback(() => {
    setCounter((counter) => counter + 1)
  }, [])

  const scrollHandler = useCallback(() => {
    if (infiniteScrollContainerRef.current) {
      //FOR MESSAGE JUMPER
      const scrollPosition = 0 - infiniteScrollContainerRef.current.scrollTop
      const scrollHeight = infiniteScrollContainerRef.current.scrollHeight
      // const clientHeight = infiniteScrollContainerRef.current.clientHeight

      if (scrollPosition > scrollHeight * 0.01) {
        setShowScrollButton(true)
        setScrollAboveThreshold(true)
      }
      if (scrollPosition <= scrollHeight * 0.01) {
        setCounter(0)
        setShowScrollButton(false)
        setScrollAboveThreshold(false)
      }
    }
  }, [])

  const onResetThreadId = useCallback(() => {
    setConversationSidePanel((prevState) => ({
      ...prevState,
      params: { ...prevState.params, threadId: undefined },
    }))
  }, [setConversationSidePanel])


  const group = groups.find((group: IGroup) => group._id === channel.groupId)

  return (
    <Content>
      {isShowSpinner && <Spinner position="absolute" loading={true} />}
      {visibleChannelInfo && <ChannelInfoModal onClose={toggleChannelInfo} />}
      <ScrollDownButton
        visible={showScrollButton}
        onClick={onScrollBottom}
        sidePanel={conversationSidePanel.visible}
      />
      <ConversationHeader
        type={RoomTypes.CM}
        roomName={channel.name}
        isPrivate={channel.isPrivate}
        description={channel?.members?.length + ' ' + t(TABS.MEMBERS)}
        pins={channel.pins}
        searchText={searchText}
        visibleSearchInput={isSearchInput}
        roomId={channelId}
        onVideoCall={()=>onVideoCall(channel?.members)}
        onChangeSearch={onChangeSearch}
        onOpenRoomInfo={toggleChannelInfo}
        onToggleSearchInput={setSearchInput}
        onToggleConversationPanel={onToggleConversationPanel}
        onToggleConversationSidePanel={onToggleConversationSidePanel}
      />
      {counter > 0 && (
        <>
          <Jumper
            counter={counter}
            onScrollBottom={onScrollBottom}
            onClose={onCloseMessageJumper}
            sidePanel={conversationSidePanel.visible}
          />
        </>
      )}
      <ContentBody>
        <ContentSection
          isTrue={conversationSidePanel.visible}
          onDrop={onDrop}
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onDragOver={onDragOver}
        >
          <Dropzone isDragging={isDragging} getRootProps={getRootProps} onClose={onCloseDropzone} />
          <CustomConversationScroll
            visibleOption={!channel?.isArchived && !channel?.readOnly}
            roomId={channelId}
            myId={myId}
            jumpToIndex={jumpToIndex}
            scrollRef={infiniteScrollContainerRef}
            dataLength={messagesData?.messages?.length || 0}
            data={messagesData?.messages || []}
            draftData={draftData}
            hasMore={messagesData?.hasMore === undefined ? false : messagesData.hasMore}
            selectionMode={selectionMode}
            onPagination={onPagination}
            onScrollHandler={scrollHandler}
            onDeleteMessage={onDeleteMessage}
            onResetJumpToIndex={setJumpToIndex}
            onReactionMessage={onReactionMessage}
            onTogglePinMessage={onTogglePinMessage}
            onReplyMessage={onToggleConversationSidePanel}
            setSelectionMode={setSelectionMode}
            onToggleMessageSelection={handleToggleMessageSelection}
            onReadMessage={onToggleConversationSidePanel}
          />
          {selectionMode.isSelectionMode && (
            <SelectionBar
              type={'message'}
              selectedMessages={selectionMode.selectedMessages}
              handleCancelSelection={handleCancelSelection}
              handleCompleteSelection={handleCompleteSelection}
            />
          )}
          <MessageInput
            isJoinChannel={channel?.hasSubscription === false}
            isReadOnly={channel?.readOnly}
            isArchived={channel?.isArchived}
            isAppear={channel?.isAppear}
            users={group?.userData ?? {}}
            roomId={channelId}
            channel
            groups={groups}
            placeHolder={t(PLACEHOLDERS.MESSAGE_PLACEHOLDER)}
            initialInputData={initialInputData}
            getInputProps={getInputProps}
            onUploadAudio={onUploadAudio}
            onAppearChannel={onAppearChannel}
            onUnarchiveChannel={onUnarchiveChannel}
            onJoinChannel={onJoinChannel}
            onScrollBottom={onScrollBottom}
            onUpdatePastedFileTargetId={onUpdatePastedFileTargetId}
          />
        </ContentSection>
        {showForwardToRoomsModal && (
          <Forward
            onClose={handleCloseForwardToRoomsModal}
            onForward={handleForward}
            isLoading={isForwarding}
          />
        )}
        {useMemo(() => {
          if (!conversationSidePanel.visible) return null

          return (
            <ConversationSidePanel
              title={
                conversationSidePanel.type === SIDE_PANEL_PIN
                  ? t(TITLES.PIN_TITLE)
                  : conversationSidePanel.type === SIDE_PANEL_THREAD
                  ? t(TITLES.THREAD_TITLE)
                  : conversationSidePanel.type === SIDE_PANEL_READERS
                  ? t(TITLES.MESSAGE_TITLE)
                  : t(TITLES.SEARCH_TITLE)
              }
              onClose={onToggleConversationSidePanel}
              onToggleSearchInput={setSearchInput}
              onChangeSearch={onChangeSearch}
              content={
                <>
                  {conversationSidePanel.type === SIDE_PANEL_PIN && (
                    <PinMessages
                      users={group?.userData ?? {}}
                      myId={myId}
                      onJumpMessage={onJumpMessage}
                    />
                  )}
                  {conversationSidePanel.type === SIDE_PANEL_THREAD && (
                    <ThreadMessages
                      {...conversationSidePanel.params}
                      roomId={channelId}
                      users={channelWithUsers?.userData ?? {}}
                      myId={myId}
                      isReadOnly={channel?.readOnly}
                      isArchived={channel?.isArchived}
                      onResetThreadId={onResetThreadId}
                      onReadMessage={setConversationSidePanel}
                      onUpdatePastedFileTargetId={onUpdatePastedFileTargetId}
                      selectionMode={threadSelectionMode}
                      onToggleMessageSelection={handleToggleThreadSelection}
                      setSelectionMode={setThreadSelectionMode}
                      setShowForwardToRoomsModal={setShowForwardToRoomsModal}
                    />
                  )}
                  {conversationSidePanel.type === SIDE_PANEL_SEARCH && (
                    <SearchMessages
                      value={searchText}
                      roomId={channelId}
                      users={group?.userData ?? {}}
                      myId={myId}
                      onJumpMessage={onJumpMessage}
                    />
                  )}
                  {conversationSidePanel.type === SIDE_PANEL_READERS && (
                <ReadStatusPanel
                message={(msg => msg && {
                  ...msg,
                  readers: (msg.readers || []).filter((id: any) => id?.toString() !== myId?.toString())
                })(
                  messagesData?.messages?.find(
                    (message: any) => message._id == conversationSidePanel.params?.messageId
                  )
                )}
                users={Object.fromEntries(
                  Object.entries(channelWithUsers?.userData ?? {}).filter(([id]) => id !== myId)
                )}
                myId={myId}
              />
                  )}
                </>
              }
            />
          )
        }, [
          conversationSidePanel.visible,
          conversationSidePanel.type,
          conversationSidePanel.params,
          channelId,
          group?.userData,
          channelWithUsers?.userData,
          myId,
          channel?.readOnly,
          channel?.isArchived,
          threadSelectionMode,
          messagesData?.messages,
          searchText,
          onDeleteMessage,
          onToggleConversationSidePanel,
          onJumpMessage,
          onResetThreadId,
          setConversationSidePanel,
          onUpdatePastedFileTargetId,
          handleToggleThreadSelection,
          setThreadSelectionMode,
          setShowForwardToRoomsModal,
          onChangeSearch,
          setSearchInput,
        ])}
      </ContentBody>
      {Modal}
      {ConferenceModal}
    </Content>
  )
}

export default Conversation

const Content = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: hidden;
  width: 100%;
  height: calc(var(--vh, 1vh) * 100);
  position: relative;
`

const ContentBody = styled.div`
  display: flex;
  overflow: hidden;
  flex-grow: 1;
  position: relative;
`

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow: hidden;
  @media (max-width: ${MOBILE_WIDTH - 400}px) {
    flex-grow: 1;
    flex-direction: column;
    overflow: hidden;
    display: ${(props: { isTrue: boolean }) => (props.isTrue ? `none;` : `flex`)};
    position: ${(props: { isTrue: boolean }) => (props.isTrue ? `absolute` : `static`)};
  }
`
